import { useCallback, useState } from 'react';
import { useDropzone } from 'react-dropzone';
import { UploadCloud, FileText, X } from 'lucide-react';
import clsx from 'clsx';

interface Props {
  onFile: (file: File) => void;
  accept?: Record<string, string[]>;
  maxSizeMB?: number;
  selectedFile?: File | null;
  onClear?: () => void;
  label?: string;
  sublabel?: string;
}

export default function FileDropzone({
  onFile,
  accept = {
    'application/pdf': ['.pdf'],
    'application/vnd.openxmlformats-officedocument.wordprocessingml.document': ['.docx'],
    'text/plain': ['.txt', '.md'],
  },
  maxSizeMB = 20,
  selectedFile,
  onClear,
  label = 'Drop your report here, or click to browse',
  sublabel = 'Supports .docx, .pdf, .txt — up to 20 MB',
}: Props) {
  const [error, setError] = useState<string | null>(null);

  const onDrop = useCallback((accepted: File[], rejected: any[]) => {
    setError(null);
    if (rejected.length) {
      const reason = rejected[0]?.errors?.[0]?.message ?? 'File rejected';
      setError(reason);
      return;
    }
    if (accepted[0]) onFile(accepted[0]);
  }, [onFile]);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept,
    multiple: false,
    maxSize: maxSizeMB * 1024 * 1024,
  });

  if (selectedFile) {
    return (
      <div className="card p-4 flex items-center gap-3">
        <div className="w-11 h-11 rounded-xl bg-brand-50 text-brand-700 grid place-items-center">
          <FileText className="w-5 h-5" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="font-medium text-ink-900 truncate">{selectedFile.name}</div>
          <div className="text-xs text-ink-500">{(selectedFile.size / 1024).toFixed(0)} KB</div>
        </div>
        {onClear && (
          <button onClick={onClear} className="p-2 rounded-lg hover:bg-ink-100" aria-label="Remove file">
            <X className="w-4 h-4 text-ink-600" />
          </button>
        )}
      </div>
    );
  }

  return (
    <div
      {...getRootProps()}
      className={clsx(
        'card p-8 border-dashed border-2 text-center cursor-pointer transition-all',
        isDragActive ? 'border-brand-500 bg-brand-50/40' : 'border-ink-200 hover:border-brand-400 hover:bg-ink-50/40'
      )}
    >
      <input {...getInputProps()} />
      <div className="mx-auto w-14 h-14 rounded-2xl bg-brand-50 text-brand-700 grid place-items-center">
        <UploadCloud className="w-6 h-6" />
      </div>
      <p className="mt-4 font-medium text-ink-900">{label}</p>
      <p className="text-sm text-ink-500 mt-1">{sublabel}</p>
      {error && <p className="mt-3 text-sm text-red-600">{error}</p>}
    </div>
  );
}
